import React, { useState, useRef, useEffect } from 'react';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import './ErrorCard.css';

const LEVEL_CONFIG = {
  '一级': { label: 'Critical', className: 'level-critical' },
  '二级': { label: 'Major', className: 'level-major' },
  '三级': { label: 'Minor', className: 'level-minor' },
};

export default function ErrorCard({ card, taskUuid, initialState = {}, onViewed }) {
  const [viewed, setViewed] = useState(!!initialState.viewed);
  const [liked, setLiked] = useState(!!initialState.up);
  const [disliked, setDisliked] = useState(!!initialState.down);
  const cardRef = useRef(null);
  const viewedFired = useRef(!!initialState.viewed);

  useEffect(() => {
    if (viewedFired.current || !cardRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewedFired.current) {
          viewedFired.current = true;
          setViewed(true);
          sendReaction('viewed', true);
          if (onViewed) onViewed(card.card_id);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [card.card_id, onViewed]);
  const [expanded, setExpanded] = useState(false);

  const imageEn = card.image_en;
  const imageCn = card.image_cn;

  const levelInfo = LEVEL_CONFIG[card.level] || { label: card.level, className: 'level-minor' };

  const sendReaction = async (type, active) => {
    try {
      await fetch(`/api/v1/stream/${taskUuid}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card_id: card.card_id, type, active }),
      });
    } catch (e) {
      console.error('Reaction failed:', e);
    }
  };

  return (
    <div ref={cardRef} className={`error-card ${disliked ? 'error-card--rejected' : ''} ${viewed ? 'error-card--viewed' : ''}`}>
      <div className="error-card__header">
        <span className={`error-card__level ${levelInfo.className}`}>
          {card.level}
        </span>
        <span className="error-card__page">
          EN p{card.page_en} / CN p{card.page_cn}
        </span>
        <label
          className={`error-card__viewed ${viewed ? 'error-card__viewed--active' : ''}`}
          title="Mark as reviewed/fixed"
        >
          <input
            type="checkbox"
            checked={viewed}
            onChange={() => {
              const next = !viewed;
              setViewed(next);
              viewedFired.current = next;
              sendReaction('viewed', next);
            }}
          />
          Viewed
        </label>
      </div>

      <div className="error-card__images" onClick={() => setExpanded(!expanded)}>
        <div className="error-card__img-wrapper">
          <img
            src={imageEn}
            alt={`EN page ${card.page_en}`}
            loading="lazy"
            className={`error-card__img${expanded ? ' error-card__img--expanded' : ''}`}
          />
          <span className="error-card__img-label">English</span>
        </div>
        <div className="error-card__img-wrapper">
          <img
            src={imageCn}
            alt={`CN page ${card.page_cn}`}
            loading="lazy"
            className={`error-card__img${expanded ? ' error-card__img--expanded' : ''}`}
          />
          <span className="error-card__img-label">Chinese</span>
        </div>
      </div>

      <div className="error-card__details">
        <div className="error-card__row">
          <span className="error-card__label">Error</span>
          <span className="error-card__value">{card.reason}</span>
        </div>
        <div className="error-card__row">
          <span className="error-card__label">Fix</span>
          <span className="error-card__value">{card.suggestion}</span>
        </div>
      </div>

      <div className="error-card__feedback">
        <button
          className={`error-card__fb-btn ${liked ? 'error-card__fb-btn--up' : ''}`}
          onClick={() => { setLiked(!liked); sendReaction('up', !liked); }}
          title="Helpful"
        >
          <FaThumbsUp />
        </button>
        <button
          className={`error-card__fb-btn ${disliked ? 'error-card__fb-btn--down' : ''}`}
          onClick={() => { setDisliked(!disliked); sendReaction('down', !disliked); }}
          title="Not helpful"
        >
          <FaThumbsDown />
        </button>
      </div>
    </div>
  );
}
